import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Cart.module.css';
import CartPagePlaceholder from './CartPagePlaceholder';
import CartItemCard from './CartItemCard';
import type { ICartPageProps, ICartItemData } from '@model/Cart.model';
import { formatMoney } from '../../../Logic/Functions/UtilsFunctions';
import { useCartState } from '../../../Logic/Functions/Hooks/useCartState';
import { CartService } from '../../../Logic/CartService';
import { UserAuthenticationStoreImplementation } from 'Data/UserAuthentication/UserAuthenticationStoreImplementation';
import { useForceLogoutAndRedirect } from '../../../Logic/Functions/Hooks/useForceLogoutAndRedirect';
import { OrderStoreImplementation } from 'Data/Order/OrderStoreImplementation';

const CartPage: React.FC<ICartPageProps> = ({ loading = false }) => {
  const navigate = useNavigate();
  const userAuthStore = UserAuthenticationStoreImplementation();
  const authToken = userAuthStore.authenticationState?.token ?? '';
  const isLoggedIn = !!authToken.trim();
  const cartService = useMemo(() => new CartService(), []);
  const guest = useCartState({ isLoggedIn: false });
  const onAuthError = useForceLogoutAndRedirect();
  const { setOrderItems } = OrderStoreImplementation();
  const [accountItems, setAccountItems] = useState<ICartItemData[]>([]);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [itemErrors, setItemErrors] = useState<Record<string, string | null>>({});
  const [accountSelectedIds, setAccountSelectedIds] = useState<Set<string>>(new Set());
  const didAutoSelectRef = useRef(false);

  useEffect(() => {
    if (!isLoggedIn || !authToken) return;
    const load = async () => {
      await cartService.handleGetCart({
        token: authToken,
        setLoading: setAccountLoading,
        setApiError: setAccountError,
        setItems: setAccountItems,
        onAuthError,
      });
    };
    load();
    const onChanged = () => void load();
    window.addEventListener('cart:changed', onChanged);
    return () => window.removeEventListener('cart:changed', onChanged);
  }, [isLoggedIn, authToken, cartService]);

  useEffect(() => {
    if (!isLoggedIn) return;
    if (didAutoSelectRef.current) return;
    if (accountItems.length > 0 && accountSelectedIds.size === 0) {
      setAccountSelectedIds(new Set(accountItems.map((i) => i.id)));
      didAutoSelectRef.current = true;
    }
  }, [isLoggedIn, accountItems, accountSelectedIds.size]);

  const view = useMemo(() => {
    if (isLoggedIn) {
      const items = accountItems;
      const allSelected = items.length > 0 && items.every((i) => accountSelectedIds.has(i.id));
      const noneSelected = accountSelectedIds.size === 0;
      const toggleOne = (id: string) => {
        setAccountSelectedIds((prev) => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          return next;
        });
        didAutoSelectRef.current = true;
      };
      const setAll = (checked: boolean) => {
        setAccountSelectedIds(checked ? new Set(items.map((i) => i.id)) : new Set());
        didAutoSelectRef.current = true;
      };
      const selectedSubtotal = items
        .filter((i) => accountSelectedIds.has(i.id))
        .reduce(
          (sum, it) => sum + Number(it.lineTotal || (it.price || 0) * (Number(it.qty) || 0)),
          0
        );
      const currency = (items[0]?.currency as string) || 'USD';
      return {
        mode: 'account' as const,
        items,
        selectedIds: accountSelectedIds,
        allSelected,
        noneSelected,
        toggleOne,
        setAll,
        selectedSubtotal,
        currency,
      };
    }
    return {
      mode: 'guest' as const,
      items: guest.items,
      selectedIds: guest.selectedIds,
      allSelected: guest.allSelected,
      noneSelected: guest.noneSelected,
      toggleOne: guest.toggleOne,
      setAll: guest.setAll,
      selectedSubtotal: guest.selectedSubtotal,
      currency: guest.currency || 'USD',
    };
  }, [
    isLoggedIn,
    accountItems,
    accountSelectedIds,
    guest.items,
    guest.selectedIds,
    guest.allSelected,
    guest.noneSelected,
    guest.toggleOne,
    guest.setAll,
    guest.selectedSubtotal,
    guest.currency,
  ]);

  const totalCartQuantity = useMemo(
    () => view.items.reduce((sum, it) => sum + Number(it.qty || 0), 0),
    [view.items]
  );

  const isLoading = loading || (isLoggedIn ? accountLoading : false);
  const apiError = isLoggedIn ? accountError : null;

  const handleProceedToCheckout = () => {
    cartService.handleProceedToCheckout({
      cartItems: view.items,
      selectedIds: view.selectedIds,
      setOrderItems,
      navigate,
    });
  };

  const handleDelete = async (item: ICartItemData) => {
    if (view.mode === 'account' && item.cartItemId) {
      await cartService.handleDeleteItem({
        token: authToken,
        cartItemId: item.cartItemId,
        setLoading: setAccountLoading,
        setApiError: setAccountError,
        setItems: setAccountItems,
        onAuthError,
      });
      setAccountSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      didAutoSelectRef.current = true;
    } else {
      guest.deleteOne(item.id);
    }
  };

  const handleQtyChange = async (item: ICartItemData, nextQty: number) => {
    setItemErrors((prev) => ({ ...prev, [item.id]: null }));
    if (view.mode === 'account' && item.cartItemId) {
      try {
        const err = await cartService.updateAccountQuantityWithValidation({
          token: authToken,
          item,
          nextQty,
          setLoading: setAccountLoading,
          setApiError: setAccountError,
          setItems: setAccountItems,
          onAuthError,
        });
        if (err) {
          setItemErrors((prev) => ({ ...prev, [item.id]: err }));
          return;
        }
        didAutoSelectRef.current = true;
      } catch {
        setItemErrors((prev) => ({
          ...prev,
          [item.id]: 'Could not update quantity. Please try again.',
        }));
      }
    } else {
      const err = cartService.updateGuestQuantityWithValidation({
        item,
        nextQty,
        changeQty: guest.changeQty,
      });
      if (err) {
        setItemErrors((prev) => ({ ...prev, [item.id]: err }));
        return;
      }
    }
  };

  if (isLoading) {
    return <CartPagePlaceholder title="My Cart" placeholderCount={3} />;
  }

  return (
    <main className={styles.cartPage} data-testid="cart-page">
      <article className={styles.cartContainer}>
        <section className={styles.cartHeader}>
          <h2 data-testid="cart-title">My Cart</h2>
          {view.items.length > 0 && (
            <span className={styles.totalCount} data-testid="cart-total-count">
              ({totalCartQuantity} item{totalCartQuantity !== 1 ? 's' : ''})
            </span>
          )}
          {view.items.length > 0 && (
            <label className={styles.bulkSelect}>
              <input
                type="checkbox"
                checked={view.allSelected}
                onChange={(e) => view.setAll(e.target.checked)}
                data-testid="cart-select-all"
              />
              <span>
                {view.allSelected
                  ? ' Unselect all'
                  : view.noneSelected
                    ? ' Select items'
                    : ' Select all'}
              </span>
            </label>
          )}
        </section>

        <section className={styles.cartItems} data-testid="cart-items">
          {apiError ? (
            <p className={styles.errorText}>{apiError}</p>
          ) : view.items.length === 0 ? (
            <p className={styles.emptyCart} data-testid="empty-cart">
              Your cart is empty!
            </p>
          ) : (
            view.items.map((item) => (
              <div key={item.id}>
                <CartItemCard
                  item={item}
                  checked={view.selectedIds.has(item.id)}
                  onToggle={() => view.toggleOne(item.id)}
                  onQtyChange={(q) => handleQtyChange(item, q)}
                  onDelete={() => handleDelete(item)}
                  formatMoney={formatMoney}
                />
                {itemErrors[item.id] && <p className={styles.errorText}>{itemErrors[item.id]}</p>}
              </div>
            ))
          )}
        </section>

        <section className={styles.cartFooter}>
          <div className={styles.cartSummary} data-testid="cart-summary">
            <div className={styles.summaryRow}>
              <span>Selected</span>
              <strong
                className={styles.tnum}
              >{`${view.selectedIds.size}/${view.items.length}`}</strong>
            </div>
            <div className={styles.summaryRow}>
              <span>Subtotal</span>
              <strong className={styles.tnum} data-testid="cart-subtotal">
                {formatMoney(view.selectedSubtotal, view.currency)}
              </strong>
            </div>
          </div>

          <button
            type="button"
            className={styles.checkoutBtn}
            disabled={
              (isLoggedIn ? view.items.length === 0 : guest.disableCheckout) ||
              view.selectedIds.size === 0
            }
            onClick={handleProceedToCheckout}
            data-testid="checkout-btn"
          >
            Checkout
          </button>

          {view.noneSelected && view.items.length > 0 && (
            <p className={styles.helperNote}>
              Select items you want to buy now — unselected items stay in your cart.
            </p>
          )}
        </section>
      </article>
    </main>
  );
};

export default CartPage;
